import { useState, ReactElement, useRef, useEffect, useLayoutEffect } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "motion/react"
import { RightArrow } from "./svgCollection"
import Modal from "./Modal"

const DISPLAY = "'Tilda Sans', sans-serif"

const SHEET_BG = "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)"

function useIsMobile() {
    const [mobile, setMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    )

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)")
        const update = () => setMobile(mq.matches)
        mq.addEventListener("change", update)
        return () => mq.removeEventListener("change", update)
    }, [])

    return mobile
}

type NavigateMode = "push" | "replace"

export type StepProps<T> = {
    params: T
    navigate: (id: string, update?: Partial<T>, mode?: NavigateMode) => void
    goBack: () => void
}

export type StepConfig<T> = {
    id: string
    title: string
    component: (props: StepProps<T>) => ReactElement
    /** Экран рисует свой заголовок (как Seats в покупке билета) */
    selfHeader?: boolean
}

type StepperProps<T> = {
    steps: StepConfig<T>[]
    initialStep?: string
    initialStack?: string[]
    overlay?: boolean
    /** На mobile: модалка как bottom sheet, контент у нижнего края */
    sheet?: boolean
    /** Узкая модалка на desktop */
    compact?: boolean
    initialData?: Partial<T>
    onStateChange?: (state: { stack: string[]; data: T }) => void
    /** Закрытие модалки с корневого шага (вместо navigate(-1)) */
    onDismiss?: () => void
}

export function Stepper<T>({
    steps,
    initialStep,
    initialStack,
    overlay,
    sheet,
    compact,
    initialData,
    onStateChange,
    onDismiss,
}: StepperProps<T>) {
    const isMobile = useIsMobile()
    const [data, setData] = useState<T>({ ...(initialData ?? {}) } as T)
    const [stack, setStack] = useState<string[]>(() => {
        if (initialStack?.length) return initialStack
        return overlay ? [] : [initialStep ?? steps[0].id]
    })
    const router = useNavigate()

    const containerRef = useRef<HTMLDivElement>(null)
    const [lockedHeight, setLockedHeight] = useState<number | null>(null)

    const initialRef = useRef<HTMLDivElement>(null)
    const overlayRef = useRef<HTMLDivElement>(null)

    const [syncHeight, setSyncHeight] = useState<number | null>(null)
    const overlayOpen = stack.length > 0

    useEffect(() => {
        if (!overlay || sheet) return
        const initialEl = initialRef.current
        const overlayEl = overlayRef.current

        if (!initialEl) return

        const ro = new ResizeObserver(() => {
            const h1 = initialEl.offsetHeight
            const h2 = overlayEl?.offsetHeight ?? 0
            if (stack.length === 0) {
                setSyncHeight(null)
            } else {
                setSyncHeight(Math.max(h1, h2))
            }
        })

        ro.observe(initialEl)
        if (overlayEl) ro.observe(overlayEl)

        return () => ro.disconnect()
    }, [stack, overlay, sheet])

    const initialStepId = initialStep ?? steps[0].id
    const initial = steps.find(s => s.id === initialStepId)!

    const navigate = (id: string, update?: Partial<T>, mode: NavigateMode = "push") => {
        if (!steps.some(s => s.id === id)) {
            console.warn(`Step "${id}" not found`)
            return
        }

        if (update) {
            setData(prev => ({ ...prev, ...update }))
        }

        setStack(prev => {
            if (mode === "replace") {
                if (prev.length === 0) return [id]
                return [...prev.slice(0, -1), id]
            }

            return [...prev, id]
        })
    }

    const goBack = () => {
        setStack(prev => {
            const limit = overlay ? 0 : 1
            if (prev.length > limit) {
                return prev.slice(0, -1)
            }
            if (onDismiss) {
                onDismiss()
            } else {
                router(-1)
            }
            return prev
        })
    }

    const currentStepId = stack.length > 0 ? stack[stack.length - 1] : initialStepId
    const current = steps.find(s => s.id === currentStepId)!
    const StepComponent = current.component
    const InitialComponent = initial.component
    const paymentRedirectUrl = (data as { paymentRedirectUrl?: string }).paymentRedirectUrl
    const isCardPaymentOverlay =
        overlayOpen && currentStepId === "payStatus" && typeof paymentRedirectUrl === "string" && paymentRedirectUrl.length > 0

    useEffect(() => {
        onStateChange?.({ stack, data })
    }, [stack, data, onStateChange])

    useEffect(() => {
        if (overlay || isMobile) return
        setLockedHeight(null)
    }, [currentStepId, overlay, isMobile])

    useLayoutEffect(() => {
        if (overlay || isMobile || lockedHeight !== null) return
        const el = containerRef.current
        if (!el) return
        setLockedHeight(el.offsetHeight)
    }, [lockedHeight, currentStepId, overlay, isMobile])

    const Header = ({ compactTop = false }: { compactTop?: boolean }) => (
        <header
            className={`flex shrink-0 items-center justify-between border-b border-white/10 px-5 ${compactTop ? "h-[60px] pt-2" : "h-14"}`}
            style={{ background: SHEET_BG }}>
            <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={goBack}
                className="flex size-9 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/[0.06] transition-colors hover:bg-white/[0.12] cursor-pointer">
                <div className="rotate-180">
                    <RightArrow />
                </div>
            </motion.button>
            <div
                className="text-[17px] font-semibold tracking-tight text-white"
                style={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: "-0.02em" }}>
                {current.title}
            </div>
            <div className="size-9 shrink-0 opacity-0" />
        </header>
    )

    return (
        <Modal sheet={sheet} compact={compact} hideHandle={Boolean(sheet && overlayOpen && !isCardPaymentOverlay)} onDismiss={onDismiss}>
            {overlay ? (
                <div className="relative flex h-full min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden">
                    <div
                        className={
                            sheet
                                ? "z-10 flex h-full min-h-0 w-full flex-col overflow-hidden md:h-full md:overflow-hidden"
                                : "z-10 flex h-full flex-col justify-center"
                        }
                        style={{
                            minHeight: sheet ? undefined : syncHeight ?? "auto",
                            cursor: overlayOpen ? "pointer" : "default",
                        }}
                        ref={initialRef}>
                        <InitialComponent params={data} navigate={navigate} goBack={goBack} />
                    </div>
                    {overlayOpen && (
                        <div
                            className={
                                sheet
                                    ? isCardPaymentOverlay
                                        ? "absolute inset-0 z-20 flex flex-col overflow-hidden"
                                        : "absolute inset-0 z-20 flex max-w-full flex-col overflow-hidden md:flex-row md:cursor-pointer md:items-stretch md:justify-end md:bg-[rgba(10,18,42,0.6)] md:backdrop-blur-sm"
                                    : "absolute inset-0 z-20 flex cursor-pointer items-end justify-end md:items-stretch"
                            }
                            style={sheet ? undefined : { background: "rgba(10,18,42,0.6)", backdropFilter: "blur(4px)" }}
                            onMouseDown={e => {
                                if (isCardPaymentOverlay) return
                                if (sheet) {
                                    if (window.matchMedia("(min-width: 768px)").matches && e.target === e.currentTarget) {
                                        goBack()
                                    }
                                    return
                                }
                                goBack()
                            }}>
                            <div
                                className={
                                    sheet
                                        ? isCardPaymentOverlay
                                            ? "pointer-events-auto flex h-full min-h-0 w-full flex-col overflow-hidden"
                                            : "pointer-events-auto flex h-full min-h-0 w-full min-w-0 max-w-full flex-col overflow-hidden md:h-full md:max-h-none md:w-[428px] md:shrink-0 md:rounded-none md:rounded-l-[24px]"
                                        : "pointer-events-auto flex max-h-[88svh] w-full cursor-auto flex-col overflow-auto rounded-t-[24px] md:h-full md:max-h-none md:w-[428px] md:shrink-0 md:rounded-none md:rounded-l-[24px]"
                                }
                                ref={overlayRef}
                                style={{
                                    background: SHEET_BG,
                                    borderTop: sheet ? undefined : "1.5px solid rgba(255,255,255,0.12)",
                                    borderLeft: isCardPaymentOverlay ? undefined : "1.5px solid rgba(255,255,255,0.08)",
                                }}
                                onMouseDown={e => e.stopPropagation()}>
                                {!current.selfHeader && !isCardPaymentOverlay && <Header compactTop={Boolean(sheet)} />}
                                <div className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden">
                                    <StepComponent params={data} navigate={navigate} goBack={goBack} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : current.selfHeader ? (
                <div ref={containerRef} className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <StepComponent params={data} navigate={navigate} goBack={goBack} />
                </div>
            ) : (
                <div
                    ref={containerRef}
                    style={!isMobile && lockedHeight ? { height: lockedHeight } : undefined}
                    className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <Header />
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[max(1.5rem,var(--safe-bottom))] [-webkit-overflow-scrolling:touch]">
                        <StepComponent params={data} navigate={navigate} goBack={goBack} />
                    </div>
                </div>
            )}
        </Modal>
    )
}
